/**
 * Recorded positions for playback.
 *
 * Flat `Float64Array`, `[tS, x0, y0, x1, y1, …]` per frame, so the stepping loop
 * writes numbers into existing memory instead of building objects
 * (ARCHITECTURE.md §5.6). The renderer interpolates between frames and never
 * extrapolates past the last one.
 */
import type { Keyframes } from './types.ts';

const INITIAL_FRAMES = 512;

export interface KeyframeWriter {
  push(tS: number, xM: Float64Array, yM: Float64Array): void;
  lastTimeS(): number;
  finish(): Keyframes;
}

export function createWriter(ballCount: number): KeyframeWriter {
  const stride = 1 + 2 * ballCount;
  let data = new Float64Array(stride * INITIAL_FRAMES);
  let count = 0;

  return {
    push(tS, xM, yM) {
      if ((count + 1) * stride > data.length) {
        // Amortised doubling. Not a per-step allocation: it happens O(log n) times
        // per shot, not 7,200 times.
        const grown = new Float64Array(data.length * 2);
        grown.set(data);
        data = grown;
      }
      let at = count * stride;
      data[at++] = tS;
      for (let i = 0; i < ballCount; i++) {
        data[at++] = xM[i] ?? 0;
        data[at++] = yM[i] ?? 0;
      }
      count++;
    },
    lastTimeS() {
      return count === 0 ? Number.NEGATIVE_INFINITY : (data[(count - 1) * stride] ?? 0);
    },
    finish() {
      return { count, ballCount, stride, data: data.subarray(0, count * stride) };
    },
  };
}

export function frameTimeS(keyframes: Keyframes, frame: number): number {
  return keyframes.data[frame * keyframes.stride] ?? 0;
}

export function frameBallX(keyframes: Keyframes, frame: number, ball: number): number {
  return keyframes.data[frame * keyframes.stride + 1 + ball * 2] ?? 0;
}

export function frameBallY(keyframes: Keyframes, frame: number, ball: number): number {
  return keyframes.data[frame * keyframes.stride + 2 + ball * 2] ?? 0;
}
